// Live pager card mirroring the mock's right-column layout in
// oasis-handoff-bekk/01-simulatie-meldkamer/files/src/vnog/components/SimulationManager.tsx.
//
// Source mapping vs. mock:
//   - PhaseStepper             -> 6-step Globale_fase timeline (discrete, no intra-phase progress).
//   - Priority / BON info-boxes -> omitted (not present in backend).
//   - Classification info-box   -> kept, full width.
//   - Aangeroepen wagens box    -> same first-unit-always-visible +
//                                  collapsible expansion, populated from pager.units.
//   - Time display              -> live simulation elapsed (same as navbar WS timer).

import { useEffect, useRef, useState } from "react";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { PagerSnapshot, PagerUnit, PagerUnitStatus } from "../types";
import PhaseStepper from "./PhaseStepper";
import { getPagerPhaseIndex, parseGlobaleFase } from "../utils/phases";

interface PagerProps {
    pager: PagerSnapshot | null | undefined;
    /** Pre-formatted elapsed string from the simulation timer WS (navbar source). */
    simulationElapsed?: string;
}

const EmptyPager = ({ reason }: { reason: string }) => (
    <div className="h-full flex flex-col items-center justify-center text-sm text-gray-400 italic border border-dashed border-gray-200 rounded-lg p-6 gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Pager</span>
        <span>{reason}</span>
    </div>
);

const STATUS_BADGE_CLASS: Record<PagerUnitStatus, string> = {
    Alarm: "bg-red-100 text-red-800 border-red-200",
    Uitgerukt: "bg-orange-100 text-orange-800 border-orange-200",
    Terplaatse: "bg-green-100 text-green-800 border-green-200",
    Ingerukt: "bg-blue-100 text-blue-800 border-blue-200",
    Terug: "bg-gray-100 text-gray-700 border-gray-200",
};

// Matches the mock's `infoBoxBase` shape: short fixed-height pill row.
const INFO_BOX_BASE = "h-9 inline-flex items-center px-3 rounded border text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis";

const SimulationTimeDisplay = ({ elapsed }: { elapsed: string }) => (
    <div className="w-full font-mono text-3xl tabular-nums bg-gray-50 text-gray-900 py-2 rounded border border-gray-200 text-center flex-shrink-0">
        {elapsed}
    </div>
);

const formatGrip = (value: string | number | null | undefined): string | null => {
    if (value == null) return null;
    const str = String(value).trim();
    if (!str || str === "0") return null;
    return `GRIP ${str}`;
};

const formatClassification = (
    mc: PagerSnapshot["meldingsclassificatie"]
): string => {
    if (!mc) return "—";
    if (mc.chain && mc.chain.length > 0) return mc.chain.join(" › ");
    return mc.raw || "—";
};

// One row inside the collapsible "Aangeroepen wagens" box. Visual contract
// mirrors the mock: monospace capcode pill, then a description that fills
// the remaining horizontal space, and a small status badge on the right.
const UnitRow = ({ unit }: { unit: PagerUnit }) => {
    const badgeClass = unit.current_status
        ? STATUS_BADGE_CLASS[unit.current_status]
        : "bg-gray-100 text-gray-500 border-gray-200";
    const description = [unit.eenheid_type, unit.kazerne]
        .filter(Boolean)
        .join(" · ") || "—";
    return (
        <div className="flex items-center gap-4 px-4 py-3 text-sm">
            <span className="font-mono text-xs px-2 py-0.5 bg-white border border-gray-200 rounded text-gray-700 whitespace-nowrap">
                {unit.eenheid}
            </span>
            <span className="text-gray-800 flex-1 min-w-0 truncate">{description}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${badgeClass}`}>
                {unit.current_status || "—"}
            </span>
        </div>
    );
};

const Pager = ({ pager, simulationElapsed = "00:00:00" }: PagerProps) => {
    const [unitsExpanded, setUnitsExpanded] = useState(false);
    // Glow the wagens box for ~3s whenever a new unit arrives, same UX
    // pattern as the mock's `unitGlow`.
    const [unitGlow, setUnitGlow] = useState(false);
    const prevUnitCountRef = useRef<number>(0);

    const hasPager = pager && typeof pager === 'object' && Object.keys(pager).length > 0;

    const units = (hasPager && pager.units) || [];
    const phaseIndex = hasPager
        ? getPagerPhaseIndex(parseGlobaleFase(pager.globale_fase))
        : null;
    const grip = hasPager ? formatGrip(pager.grip) : null;
    const classification = hasPager ? formatClassification(pager.meldingsclassificatie) : "—";

    useEffect(() => {
        const prev = prevUnitCountRef.current;
        if (units.length > prev) {
            setUnitGlow(true);
            const t = setTimeout(() => setUnitGlow(false), 3000);
            prevUnitCountRef.current = units.length;
            return () => clearTimeout(t);
        }
        prevUnitCountRef.current = units.length;
    }, [units.length]);

    if (!pager) {
        return (
            <div className="h-full flex flex-col pt-[22px] pb-4 gap-3">
                <EmptyPager reason="Geen characteristics estimation geselecteerd." />
                <SimulationTimeDisplay elapsed={simulationElapsed} />
            </div>
        );
    }
    if (!hasPager) {
        return (
            <div className="h-full flex flex-col pt-[22px] pb-4 gap-3">
                <EmptyPager reason="Deze estimation heeft nog geen pager-snapshot (oudere run)." />
                <SimulationTimeDisplay elapsed={simulationElapsed} />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col gap-3 pt-[22px] pb-4">
            {/* Fase-stepper: discrete phases from Globale_fase (no intra-phase progress). */}
            <div className="border border-gray-200 rounded px-3 py-3">
                <PhaseStepper currentIndex={phaseIndex} />
            </div>

            {/* Info-box row: GRIP (optional, narrow) + hardcoded BON-01 + classification (full width). */}
            <div className="flex gap-2 items-stretch">
                {grip && (
                    <div className={`${INFO_BOX_BASE} bg-purple-50 border-purple-200 text-purple-800 flex-shrink-0`}>
                        {grip}
                    </div>
                )}
                <div className={`${INFO_BOX_BASE} bg-blue-50 border-blue-200 text-blue-800 flex-shrink-0`}>
                    BON-01
                </div>
                <div className={`${INFO_BOX_BASE} bg-gray-50 border-gray-200 text-gray-900 flex-1 min-w-0`}
                     title={classification}>
                    {classification}
                </div>
            </div>

            {/* Aangeroepen wagens — same UX as mock: always show first row,
                chevron to expand the rest as an overlay over the time block. */}
            <div className="relative">
                <div className={`border rounded-md transition-colors duration-1000 ${
                    unitGlow ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'
                }`}>
                    <button
                        type="button"
                        onClick={() => setUnitsExpanded(!unitsExpanded)}
                        className="w-full flex items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-600 hover:bg-gray-100"
                        disabled={units.length <= 1}
                    >
                        <span>Aangeroepen wagens ({units.length})</span>
                        {units.length > 1 ? (
                            unitsExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
                        ) : null}
                    </button>
                    <div className="border-t border-gray-200">
                        {units.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-400 italic">Nog geen wagens aangeroepen.</div>
                        ) : (
                            <UnitRow unit={units[0]} />
                        )}
                    </div>
                </div>
                {unitsExpanded && units.length > 1 && (
                    <ul className="absolute left-0 right-0 top-full z-10 mt-1 divide-y divide-gray-100 border border-gray-200 rounded-md bg-gray-50 shadow-lg overflow-hidden max-h-[40vh] overflow-y-auto">
                        {units.slice(1).map((u, i) => (
                            <li key={`${u.eenheid}-${i}`} className="hover:bg-gray-100">
                                <UnitRow unit={u} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Live simulation time — same source as navbar timer. */}
            <div className="mt-auto">
                <SimulationTimeDisplay elapsed={simulationElapsed} />
            </div>
        </div>
    );
};

export default Pager;
